import React, { forwardRef, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, parseSelectOptions, generateFormFields } from 'utils/formUtils';
import _ from "lodash";
import { Form } from 'antd';
import GenericMappingForm from '../GenericMappingForm';

const UpGuardMappingForm = forwardRef(function UpGuardMappingForm(props, ref) {
  const [form] = Form.useForm();
  const [labels, setLabels] = useState([]);

  const [initialValues] = useState(() => {
    if (props.record) {
      return [
        {
          product: props.defaultProduct,
          subProduct: props.defaultSubProduct,
          subProductId: props.defaultSubProductId,
          environment: props.defaultEnvironment,
          findingType: props.record.findingType || 'VENDORRISK',
        },
      ];
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
        findingType: 'VENDORRISK',
      },
    ];
  });

  const [mappedProjectList] = useState(() => {
    if (props.record) {
      const { primaryHostName, vendorName, labels: recordLabels, findingType } = props.record;
      return {
        vendor: primaryHostName ? [{ id: primaryHostName, name: vendorName }] : [],
        labels: recordLabels ? recordLabels.split(',').map(label => ({ id: label.trim(), name: label.trim() })) : [],
      }
    }
    return { vendor: [], labels: [] }
  })
  
  const optionsRef = useRef(mappedProjectList.vendor);
  const labelsRef = useRef(mappedProjectList.labels);

  const fetchProjects = useCallback((key, params) =>{
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,...params });
      promise.then((response) => {
        if(mappedProjectList[key].length > 0 && !_.isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedProjectList[key]];
        }
        optionsRef.current = _.uniqBy([...response.projects, ...optionsRef.current], 'id')
      });
      return { promise, cancel };
  },[props.loginConfigId,props.toolName, mappedProjectList])

  const fetchLabels = useCallback(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { 
      login_id: props.loginConfigId, 
      type: 'BREACHRISK' 
    });
    promise.then((response) => {
      if(mappedProjectList.labels.length > 0 && !_.isEmpty(response.projects)){
        response.projects = [...response.projects, ...mappedProjectList.labels];
      }
      const uniqueLabels = _.uniqBy([...response.projects, ...labelsRef.current], 'id');
      setLabels(uniqueLabels);
      labelsRef.current = uniqueLabels;
    });
    return { promise, cancel };
  }, [props.loginConfigId, props.toolName, mappedProjectList]);

  const fetchVendor = useCallback((params) => {
      return fetchProjects("vendor", { ...params, type: 'VENDORRISK' })
  }, [fetchProjects]);

  useEffect(() => {
    const { cancel } = fetchLabels();
    return () => {
      cancel && cancel();
    };
  }, [fetchLabels]);

  const getExtraFieldsData = useCallback((config) => {
    const { primaryHostName, labels: selectedLabels, findingType } = config;
    
    if (findingType === 'BREACHRISK') {
      return {
        labels: selectedLabels ? selectedLabels.join(',') : '',
        findingType
      };
    } else {
      const vendorName = optionsRef.current.find(vendor => vendor.id === primaryHostName)?.name
      return {
        primaryHostName, 
        vendorName,
        findingType
      };
    }
  }, [optionsRef]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { findingType, labels: configLabels, primaryHostName } = config;
    return {
      findingType,
      ...(findingType === 'BREACHRISK' && configLabels ? { labels: configLabels.split(',') } : { labels: null}),
      ...(findingType === 'VENDORRISK' && primaryHostName ? { primaryHostName } : {})
    };
  }, []);

  const projectListRender = useCallback((listField) => {
    const selectedType = form.getFieldsValue().configurations[listField.name]?.findingType;
    
    const vendorFields = [
      {
        name: [listField.name, 'primaryHostName'],
        colSpan: '12',
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: 'Vendors',
        required: true,
        props: {
            api: fetchVendor,
            allowClear: true,
            autoFocus: true,
            showDefaultError: false
        }
      }
    ];

    const breachRiskFields = [
      {
        name: [listField.name, 'labels'],
        colSpan: '12',
        type: FieldTypes.MULTI_SELECT,
        label: 'Labels',
        required: false,
        options: parseSelectOptions(labels),
        placeholder: 'Leave blank to select all',
        props: {
          autoFocus: true
        }
      }
    ];

    const formFields = selectedType === 'BREACHRISK' ? breachRiskFields : vendorFields;
    
    return (
      <div className='m-b-sm'>
        {generateFormFields(formFields)}
      </div>
    );
  }, [fetchVendor, labels, form]);

  const extraFields = useMemo(() => ([
    {
      name: 'findingType',
      type: FieldTypes.RADIO,
      options: [
        { label: 'Vendor Risk', value: 'VENDORRISK' }, 
        { label: 'Breach Risk', value: 'BREACHRISK' }
      ],
      initialValue: 'VENDORRISK',
    },
    {
      name: (listField) => [listField.name, "projectList"],
      render: projectListRender
    }
  ]), [projectListRender]);

  return (
    <GenericMappingForm
      title={<>Map {props.toolName}</>}
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props}
    />
  )
})

export default UpGuardMappingForm;
